import React, { createContext, useContext, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import InitializationLoader from 'components/InitializationLoader';
import { useUser } from 'global/UserContext';
import { useInitializationProgress } from 'hooks/useInitializationProgress';
import { isPublicRoute as checkIsPublicRoute } from 'routes/PublicRoutes';

interface InitializationContextType {
  isInitializing: boolean;
  hasInitialized: boolean;
  shouldShowLoader: boolean;
}

const InitializationContext = createContext<InitializationContextType | undefined>(undefined);

interface InitializationProviderProps {
  children: React.ReactNode;
}

/**
 * Provider centralizado que gerencia o loading de inicialização
 * Evita múltiplos AppLoading e mantém um único componente fixo
 */
export const InitializationProvider: React.FC<InitializationProviderProps> = ({ children }) => {
  const { isInitializing, hasInitialized } = useInitializationProgress();
  const { token, user } = useUser();
  const location = useLocation();
  const [shouldShowLoader, setShouldShowLoader] = useState(true);
  const [hasEverInitialized, setHasEverInitialized] = useState(false);

  // Check if current route is public (includes dynamic routes like /invite/event/:uuid)
  const isPublicRoute = checkIsPublicRoute(location.pathname);

  // Controla quando mostrar o loader
  useEffect(() => {
    console.log('[InitializationProvider] Verificando transição:', {
      hasInitialized,
      hasEverInitialized,
      shouldShowLoader,
    });

    if (hasInitialized && !hasEverInitialized) {
      console.log('[InitializationProvider] ✅ Inicialização detectada, iniciando transição...');
      setHasEverInitialized(true);
      // Delay para transição suave
      const timer = setTimeout(() => {
        console.log('[InitializationProvider] 🎉 Escondendo loader, mostrando aplicação!');
        setShouldShowLoader(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasInitialized, hasEverInitialized, shouldShowLoader]);

  // Se nunca inicializou e está inicializando, mostra loader
  useEffect(() => {
    if (!hasEverInitialized && isInitializing) {
      setShouldShowLoader(true);
    }
  }, [isInitializing, hasEverInitialized]);

  // Se está em rota pública, não mostra loader de inicialização
  useEffect(() => {
    console.log('[InitializationProvider] Estado atual:', {
      isPublicRoute,
      hasEverInitialized,
      isInitializing,
      hasInitialized,
      currentPath: location.pathname,
      shouldShowLoader,
      hasToken: !!token,
      hasUser: !!user,
    });

    if (isPublicRoute) {
      console.log('[InitializationProvider] Rota pública detectada, escondendo loader');
      setShouldShowLoader(false);
    } else if (!hasEverInitialized && isInitializing) {
      console.log('[InitializationProvider] Inicializando, mostrando loader');
      setShouldShowLoader(true);
    }
  }, [
    isPublicRoute,
    hasEverInitialized,
    isInitializing,
    hasInitialized,
    location.pathname,
    shouldShowLoader,
    token,
    user,
  ]);

  // Se está em /login e está inicializando, mostra loader centralizado
  // MAS só se há autenticação real (token/usuário)
  useEffect(() => {
    if (location.pathname === '/login' && isInitializing && !hasEverInitialized) {
      // Verificar se há autenticação real
      const hasRealAuth = !!(token && user);
      if (hasRealAuth) {
        console.log(
          '[InitializationProvider] Login em andamento com autenticação real, mostrando loader centralizado'
        );
        setShouldShowLoader(true);
      } else {
        console.log(
          '[InitializationProvider] Login sem autenticação real (logout), escondendo loader'
        );
        setShouldShowLoader(false);
      }
    }
  }, [location.pathname, isInitializing, hasEverInitialized, token, user]);

  // Detecta logout e esconde loader imediatamente
  useEffect(() => {
    if (location.pathname === '/login' && !token && !user && shouldShowLoader) {
      console.log('[InitializationProvider] Logout detectado, escondendo loader imediatamente');
      setShouldShowLoader(false);
    }
  }, [location.pathname, token, user, shouldShowLoader]);

  const contextValue: InitializationContextType = {
    isInitializing,
    hasInitialized,
    shouldShowLoader,
  };

  return (
    <InitializationContext.Provider value={contextValue}>
      {shouldShowLoader && <InitializationLoader />}
      {/* Sempre renderiza children, mesmo durante loading */}
      {children}
    </InitializationContext.Provider>
  );
};

export const useInitialization = (): InitializationContextType => {
  const context = useContext(InitializationContext);
  if (!context) {
    throw new Error('useInitialization deve ser usado dentro de um InitializationProvider');
  }
  return context;
};
